<template>
  <aside
    class="map-panel"
    :class="[
      props.fullScreen ? 'map-panel--full' : 'map-panel--side',
      { 'map-panel--mobile': $device.isMobile },
    ]"
  >
    <div v-if="!hasMapKey" class="map-panel__placeholder">
      <base-icon name="base/MapPin" size="48px" color="var(--black-monochrome-60)" />
      <p class="text-body-1 text-bold text-black-monochrome">
        Map preview is unavailable
      </p>
      <p class="text-body-2 text-black-monochrome-60">
        Google Maps API key is not configured. Set GM_MAP_API in your .env file
        to enable the map.
      </p>
    </div>

    <template v-else>
      <base-error-backdrop v-if="isMapError" />
      <base-loader v-if="isLoader" hint="Map is loading..." />

      <div ref="googleMapRef" class="map-panel__canvas" />
    </template>

    <!-- Mobile only: tapping a pin shows the unit as a bottom sheet covering the
         bottom of the screen (the list/map toggle pill is hidden while open). -->
    <Transition name="map-sheet">
      <div
        v-if="props.fullScreen && selectedUnit"
        class="map-panel__sheet"
        @touchstart.passive="onSheetTouchStart"
        @touchmove.passive="onSheetTouchMove"
        @touchend="onSheetTouchEnd"
      >
        <span class="map-panel__sheet-grip" aria-hidden="true" />
        <button
          type="button"
          class="map-panel__sheet-close"
          :aria-label="$t('directory.filters.close_card')"
          @click="closeSheet"
        >
          <base-icon name="base/Close" size="18px" color="var(--text-primary)" />
        </button>
        <listing-card
          :estate="selectedUnit"
          variant="tile"
          property-type="estates"
          source="map"
        />
      </div>
    </Transition>
  </aside>
</template>

<script setup lang="ts">
import {
  useGoogleMapHTML,
  useGoogleMapFactory,
} from "~/utilities/composables/useGoogleMaps";
import type { AppContext } from "vue";
import { Api } from "~/services/swagger/Api";
import type { UpdatedUnitResponseDTO } from "~/store/unitPage";
import eventBus from "~/utilities/composables/eventBus";

interface Props {
  fullScreen?: boolean;
}

const props = defineProps<Props>();
defineEmits<{ close: [] }>();

const apiClient = new Api();
const route = useRoute();
const config = useRuntimeConfig();

interface EstateItem {
  estate: UpdatedUnitResponseDTO;
  size: "suggestions";
  propertyType: string;
  position: google.maps.LatLng;
}

const googleMapRef = ref<HTMLElement | null>(null);
const isMapError = ref(false);
const loadCounter = ref(0);
const isLoader = ref(false);
const estates = ref<EstateItem[]>([]);
const hasMapKey = computed(() => !!config.public.gmAPI);

const loadMap = async () => {
  if (!googleMapRef.value) {
    isMapError.value = true;
    return;
  }
  loadCounter.value++;
  useGoogleMapHTML().value = googleMapRef.value;
  const { vueApp } = useNuxtApp();
  const appContext = vueApp._context as AppContext;
  const map = await useGoogleMapFactory({
    mapHTML: googleMapRef.value,
    type: "dynamic",
    points: estates.value,
    appContext,
    options: {
      noControls: true,
      isInfo: true,
      customControls: true,
    },
  });

  isLoader.value = false;
  if (!map) {
    isMapError.value = true;
    if (loadCounter.value < 3) {
      setTimeout(() => {
        loadMap();
      }, 4000);
    }
  } else {
    isMapError.value = false;
  }
};

const fetchEstates = async () => {
  const response = await apiClient.api.unitsControllerListPost({
    ...route.query,
    limit: 1000,
  });
  if (!response.data?.rows) return;
  const raw = response.data as unknown as {
    rows: UpdatedUnitResponseDTO[];
  };
  // Keep only items with a usable [lat, lng] pair. Items can come back with
  // `coords` missing (undefined) or as an empty array; both used to slip
  // through and blow up on `coords![0]` below ("Cannot read properties of
  // undefined (reading '0')").
  const filtered = raw.rows.filter(
    (item) =>
      Array.isArray(item.coords) &&
      item.coords.length >= 2 &&
      typeof item.coords[0] === "number" &&
      typeof item.coords[1] === "number"
  );
  estates.value = filtered.map((item) => ({
    estate: { ...item },
    size: "suggestions",
    position: new google.maps.LatLng({
      lat: item.coords![0],
      lng: item.coords![1],
    }),
    propertyType: "estates",
  }));
};

const initMap = () => {
  const { onLoaded } = useScriptGoogleMaps({
    apiKey: config.public.gmAPI,
  });
  isLoader.value = true;
  onLoaded(async (instance) => {
    (await instance.maps) as unknown as typeof google.maps;
    await fetchEstates();
    loadMap();
  });
};

// --- Mobile bottom-sheet card (shown when a map pin is tapped) ---
const selectedUnit = ref<UpdatedUnitResponseDTO | null>(null);

const onUnitSelected = (unit: unknown) => {
  selectedUnit.value = (unit as UpdatedUnitResponseDTO) ?? null;
};
const clearSelectedUnit = () => {
  selectedUnit.value = null;
};
const closeSheet = () => {
  selectedUnit.value = null;
  eventBus.emit("map-unit-closed");
};

// Swipe-down-to-close: track the vertical drag distance on the sheet.
const SHEET_SWIPE_CLOSE_PX = 60;
const sheetTouchStartY = ref<number | null>(null);
const onSheetTouchStart = (e: TouchEvent) => {
  sheetTouchStartY.value = e.touches[0]?.clientY ?? null;
};
const onSheetTouchMove = (e: TouchEvent) => {
  if (sheetTouchStartY.value == null) return;
  const delta = (e.touches[0]?.clientY ?? 0) - sheetTouchStartY.value;
  if (delta > SHEET_SWIPE_CLOSE_PX) {
    sheetTouchStartY.value = null;
    closeSheet();
  }
};
const onSheetTouchEnd = () => {
  sheetTouchStartY.value = null;
};

onMounted(() => {
  eventBus.on("map-unit-selected", onUnitSelected);
  eventBus.on("map-unit-closed", clearSelectedUnit);
  if (!hasMapKey.value) return;
  initMap();
});

onBeforeUnmount(() => {
  eventBus.off("map-unit-selected", onUnitSelected);
  eventBus.off("map-unit-closed", clearSelectedUnit);
});

watch(
  () => route.query,
  () => {
    if (!hasMapKey.value) return;
    if (!estates.value.length) return;
    fetchEstates().then(() => loadMap());
  },
  { deep: true }
);
</script>

<style scoped lang="scss">
.map-panel {
  position: relative;
  width: 100%;
  height: 100%;
  background: var(--surface-muted);
  border-radius: var(--radius-md);
  overflow: hidden;
  &--side {
    position: sticky;
    top: 116px;
    height: calc(100dvh - 132px);
  }
  &--full {
    position: fixed;
    inset: 0;
    z-index: 9000;
    border-radius: 0;
    height: 100dvh;
  }
}

.map-panel__canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.map-panel__placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  text-align: center;
  background: var(--surface-muted);
  p {
    max-width: 320px;
    margin: 0;
  }
}

// Mobile bottom-sheet card: covers the bottom of the fullscreen map, replacing
// the list/map toggle pill (which is hidden while the sheet is open).
.map-panel__sheet {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  padding: 10px 12px calc(12px + env(safe-area-inset-bottom, 0px));
  background: var(--surface);
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.18);
}

.map-panel__sheet-grip {
  display: block;
  width: 40px;
  height: 4px;
  margin: 0 auto 10px;
  border-radius: var(--radius-pill);
  background: var(--border-strong);
}

.map-panel__sheet-close {
  position: absolute;
  top: 12px;
  right: 14px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: var(--surface);
  box-shadow: var(--shadow-card);
  cursor: pointer;
}

.map-sheet-enter-active,
.map-sheet-leave-active {
  transition:
    transform 0.28s cubic-bezier(0.32, 0.72, 0, 1),
    opacity 0.28s ease;
}
.map-sheet-enter-from,
.map-sheet-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

</style>
